import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { CreateProject, ProjectDraftData } from "@workspace/contracts/projects";
import { toast } from "sonner";
import { orpc } from "@/api/orpc";
import { ProjectForm } from "@/features/project/form";
import { getError } from "@/utils/error";

export const Route = createFileRoute("/_authenticated/projects/new")({
  component: RouteComponent,
  loader: async ({ context: { orpc, queryClient } }) => {
    const [featureOptions, cityList, districtList, distanceOptions] = await Promise.all([
      queryClient.ensureQueryData(orpc.features.optionsAdmin.queryOptions({ input: {} })),
      queryClient.fetchQuery(orpc.cities.options.queryOptions({ input: {} })),
      queryClient.fetchQuery(orpc.districts.optionsAdmin.queryOptions()),
      queryClient.ensureQueryData(orpc.distances.options.queryOptions({ input: {} })),
    ]);
    return { featureOptions, cityList, districtList, distanceOptions };
  },
});

function RouteComponent() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { featureOptions, cityList, districtList, distanceOptions } = Route.useLoaderData();

  const { mutateAsync } = useMutation(orpc.admin.projects.create.mutationOptions());
  const { mutateAsync: saveDraft } = useMutation(orpc.admin.projectDrafts.create.mutationOptions());

  const onSubmit = async (value: CreateProject) => {
    try {
      await mutateAsync(value);
      toast.success("Project has been created successfully");

      queryClient.invalidateQueries({ queryKey: orpc.admin.projects.list.queryKey({ input: {} }) });

      navigate({ to: "/projects" });
    } catch (error) {
      const msg = getError(error, "Failed to create project");
      toast.error(msg);
    }
  };

  const onSaveDraft = async (data: ProjectDraftData) => {
    try {
      const draft = await saveDraft({ data });
      toast.success("Project draft has been saved");
      queryClient.invalidateQueries({ queryKey: orpc.admin.projectDrafts.list.queryKey({ input: {} }) });
      navigate({ to: "/projects/drafts" });
      return draft.data;
    } catch (error) {
      toast.error(getError(error, "Failed to save project draft"));
      return undefined;
    }
  };

  return (
    <ProjectForm
      project={undefined}
      onSubmit={onSubmit}
      onSaveDraft={onSaveDraft}
      featureOptions={featureOptions}
      cityList={cityList}
      districtList={districtList}
      distanceOptions={distanceOptions}
    />
  );
}
